/* -----------------Globals--------------- */
import {
	Redirect,
	useGlobalSearchParams,
	usePathname,
	useRouter,
} from "expo-router";

/* -----------------Child components--------------- */
import {
	Box,
	FeatureList,
	Loader,
	useBreakpoints,
	useToast,
} from "@memorang/ui";

import { useUser } from "@clerk/clerk-expo";
import {
	DEMO_EXAMS,
	isMockedData,
	appLogo as mockedApplogo,
	mockedLanding,
} from "@constants/demo";
import { OnboardingAppBar } from "@features/auth/";
import { isWeb } from "@helpers/platform";
import { useAppStore } from "@hooks/useAppStore";
import { useDemoExamStore } from "@hooks/useDemoExamStore";
/* -----------------Helpers & Hooks--------------- */
import { Landing } from "@memorang/applets";
import type { FeatureProp } from "@memorang/ui/src/components/FeatureList/FeatureList";
import { Image } from "expo-image";
import { useEffect } from "react";
import { useWindowDimensions } from "react-native";
import { useTheme } from "react-native-paper";

// biome-ignore lint/complexity/noExcessiveCognitiveComplexity: <explanation>
export default function IndexRoute() {
	const { logo: appLogo, landing } = useAppStore((store) => store.app);
	const { isSignedIn, isLoaded } = useUser();
	const {
		mdUp: isDesktopUp,
		lgUp: isDesktopLargeUp,
		smUp: isTabletUp,
		smDown: isMobileDown,
	} = useBreakpoints();
	const { width } = useWindowDimensions();
	const pathname = usePathname();
	const theme = useTheme();

	const isVerify = pathname === "/verify";

	const { setCurrentExam, currentExam } = useDemoExamStore();

	const { showToast } = useToast();

	const router = useRouter();

	const { examId } = useGlobalSearchParams<{ examId: string }>();

	useEffect(() => {
		if (examId && currentExam.id !== examId) {
			const newExam = DEMO_EXAMS.find((exam) => exam.id === examId);
			if (newExam) {
				const message = `Switched Exam from "${currentExam.name}" to "${newExam?.name}"`;
				showToast({
					message,
					duration: 1500,
				});
				setCurrentExam(newExam?.id);
			}
		}
	}, [examId, currentExam.id, setCurrentExam, showToast, currentExam.name]);

	if (!isLoaded) {
		return <Loader />;
	}
	if (isSignedIn && !isVerify) {
		return <Redirect href="/(protected)/(tabs)/home" />;
	}

	const handleNext = () => {
		router.push("/(auth)/login");
	};

	const isNativeTablet = isTabletUp && !isDesktopLargeUp && !isWeb;
	const showFeatureList =
		landing?.valueProps && isTabletUp && !isDesktopLargeUp && isWeb;
	const showRightPanel =
		isDesktopLargeUp && landing?.landingGraphics && landing?.valueProps;

	return (
		<>
			{isWeb && (
				<OnboardingAppBar showLogo style={{ backgroundColor: "transparent" }} />
			)}
			<Box flex={1} flexDirection="row">
				<Box
					flex={1}
					maxWidth={isDesktopLargeUp ? width / 2 : "100%"}
					backgroundColor={theme.colors.onPrimary}
					paddingBottom={isWeb ? 0 : 24}
					overflow="hidden"
					gap={24}
				>
					<Box
						paddingHorizontal={24}
						flex={
							isDesktopLargeUp || isMobileDown || isNativeTablet ? 1 : undefined
						}
						alignSelf={isWeb ? "center" : undefined}
						alignItems="center"
						justifyContent="center"
					>
						{!isWeb && (
							<Box
								flex={1}
								alignSelf="flex-end"
								width="100%"
								justifyContent="flex-end"
								alignItems="center"
								padding={24}
							>
								<Image
									source={{ uri: mockedApplogo }}
									style={{ width: 275, height: 152 }}
									contentFit="contain"
								/>
							</Box>
						)}
						<Landing
							appLogo={appLogo}
							handleNext={handleNext}
							valueProps={mockedLanding?.valueProps as FeatureProp[]}
							title={
								isMockedData
									? mockedLanding?.title
									: (landing?.title as {
											prefix?: string;
											decoratedText?: string;
											suffix?: string;
										})
							}
							marketText={
								isMockedData ? mockedLanding?.marketText : landing?.marketText
							}
							ctaLabel={isMockedData ? mockedLanding?.ctaLabel : undefined}
						/>
					</Box>

					{showFeatureList && (
						<Box
							width="100%"
							justifyContent="center"
							alignItems="center"
							paddingTop={24}
							paddingHorizontal={24}
						>
							<FeatureList
								valueProps={mockedLanding?.valueProps as FeatureProp[]}
							/>
						</Box>
					)}
				</Box>

				{showRightPanel && (
					<Box
						flex={1}
						justifyContent="center"
						alignItems="center"
						padding={48}
						minWidth={816}
						maxWidth={width / 2}
						gap={32}
					>
						<Image
							source={{
								uri: isMockedData
									? mockedLanding?.landingGraphics
									: landing?.landingGraphics,
							}}
							style={{
								width: "100%",
								maxWidth: 816,
								aspectRatio: 1.577,
							}}
							contentFit="contain"
						/>
						<FeatureList
							valueProps={mockedLanding?.valueProps as FeatureProp[]}
						/>
					</Box>
				)}
			</Box>
		</>
	);
}
